import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {
  constructor(private http: HttpClient) { }

  /**
   * Fetch sales report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the sales report data.
   */
  getSalesReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetSalesRpt`, {
      params: params
    });
  }

  /**
   * Fetch stock report data.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the stock report data.
   */
  getStockReport(companyId?: string, branchId?: string): Observable<any> {
    const params: any = {};
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetStockRpt`, {
      params: params
    });
  }

  getInventoryHealthReport(lowStockThreshold: number, overStockThreshold: number, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { lowStockThreshold, overStockThreshold };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetInventoryHealthRpt`, { params });
  }

  getPurchaseReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPurchaseRpt`, {
      params: params
    });
  }

  getLedger(partyId: string, startDate?: string, endDate?: string, pageNumber: number = 1, pageSize: number = 20, companyId?: string, branchId?: string, exportAll: boolean = false): Observable<any> {
    const params: any = { partyId, pageNumber, pageSize };
    if (exportAll) {
      params.exportAll = 'true';
    }
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetLedger`, {
      params: params
    });
  }

  getPurchaseReportByInvoiceNo(invoiceNo: string, startDate: string, endDate: string, companyId?: string, branchId?: string, productId?: number): Observable<any> {
    const params: any = { invoiceNo, startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    if (productId !== undefined) {
      params.productId = productId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPurchaseRptByInvoiceNo`, {
      params: params
    });
  }

  getExpenseReport(startDate: string, endDate: string, companyId?: string, branchId?: string, transactionFilter: string = 'all'): Observable<any> {
    const params: any = { startDate, endDate, transactionFilter };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetExpenseRpt`, {
      params: params
    });
  }

  /**
   * Fetch category-wise sales report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the category-wise sales report data.
   */
  getCategoryWiseSalesReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetCategoryWiseSalesRpt`, {
      params: params
    });
  }

  getBrandWiseSalesReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetBrandWiseSalesRpt`, { params });
  }

  getSellThroughReport(season?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = {};
    if (season) params.season = season;
    if (companyId) params.companyId = companyId;
    if (branchId) params.branchId = branchId;
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetSellThroughRpt`, { params });
  }

  getSeasons(companyId?: string, branchId?: string): Observable<string[]> {
    const params: any = {};
    if (companyId) params.companyId = companyId;
    if (branchId) params.branchId = branchId;
    return this.http.get<string[]>(`${environment.apiUrl}/Reports/GetSeasons`, { params });
  }

  /**
   * Fetch category-wise stock report data.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the category-wise stock report data.
   */
  getCategoryWiseStockReport(companyId?: string, branchId?: string): Observable<any> {
    const params: any = {};
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetCategoryWiseStockRpt`, {
      params: params
    });
  }

  /**
   * Fetch day-wise closing report data with pagination.
   * @param pageNumber - The page number for pagination (default: 1).
   * @param pageSize - The number of records per page (default: 10).
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the day-wise closing report data.
   */
  getDayWiseClosing(pageNumber: number = 1, pageSize: number = 10, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { 
      pageNumber: pageNumber.toString(), 
      pageSize: pageSize.toString() 
    };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetDayWiseClosing`, {
      params: params
    });
  }

  /**
   * Fetch payroll summary report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the payroll summary report data.
   */
  getPayrollSummaryReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPayrollSummary`, {
      params: params
    });
  }

  /**
   * Fetch detailed payroll report data for a specific employee.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param employeeId - The ID of the employee for detailed report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the detailed payroll report data.
   */
  getPayrollDetailedReport(startDate: string, endDate: string, employeeId: number, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate, employeeId: employeeId.toString() };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPayrollDetailed`, {
      params: params
    });
  }

  /**
   * Fetch person-wise sales report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param personId - Optional person/employee ID for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the person-wise sales report data.
   */
  getPersonWiseSalesReport(startDate: string, endDate: string, personId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (personId) {
      params.personId = personId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPersonWiseSalesRpt`, {
      params: params
    });
  }

  /**
   * Fetch month-wise person sales report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param personId - Optional person/employee ID for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the month-wise person sales report data.
   */
  getMonthWisePersonSalesReport(startDate: string, endDate: string, personId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (personId) {
      params.personId = personId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetMonthWisePersonSalesRpt`, {
      params: params
    });
  }

  /**
   * Fetch customer credit report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param customerId - Optional customer ID for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the customer credit report data.
   */
  getCustomerCreditReport(startDate: string, endDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (customerId) {
      params.customerId = customerId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetCustomerCreditRpt`, {
      params: params
    });
  }

  /**
   * Fetch customer outstanding report data.
   * @param asOfDate - The date as of which to calculate outstanding.
   * @param customerId - Optional customer ID for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the customer outstanding report data.
   */
  getCustomerOutstandingReport(asOfDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { asOfDate };
    if (customerId) {
      params.customerId = customerId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetCustomerOutstandingRpt`, {
      params: params
    });
  }

  /**
   * Fetch wholesale sales report data.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param customerId - Optional customer ID for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the wholesale sales report data.
   */
  getWholesaleSalesReport(startDate: string, endDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (customerId) {
      params.customerId = customerId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetWholesaleSalesRpt`, {
      params: params
    });
  }

  /**
   * Fetch dashboard statistics.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the dashboard stats data.
   */
  getDashboardStats(companyId?: string, branchId?: string): Observable<any> {
    const params: any = {};
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetDashboardStats`, {
      params: params
    });
  }

  /**
   * Fetch dashboard statistics filtered by date range.
   * @param startDate - The start date for filtering.
   * @param endDate - The end date for filtering.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the date-filtered dashboard stats data.
   */
  getDashboardStatsByDate(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetDashboardStatsByDate`, {
      params: params
    });
  }

  getTransporterDashboardStatsByDate(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetTransporterDashboardStatsByDate`, {
      params: params
    });
  }

  /**
   * Fetch PNL (Profit and Loss) report data with monthly breakdown.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the PNL report data with monthly details.
   */
  getPnlReport(startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPnlReport`, {
      params: params
    });
  }

  /**
   * Fetch purchase return detail report data.
   * @param returnNo - Optional return number for filtering.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the purchase return detail report data.
   */
  getPurchaseReturnDetailReport(returnNo: string, startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { returnNo, startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetPurchaseReturnDetailRpt`, {
      params: params
    });
  }

  /**
   * Fetch wholesale detail report data.
   * @param invoiceNo - Optional invoice number for filtering.
   * @param startDate - The start date for the report.
   * @param endDate - The end date for the report.
   * @param companyId - Optional company ID for filtering.
   * @param branchId - Optional branch ID for filtering.
   * @returns An observable containing the wholesale detail report data.
   */
  getWholesaleDetailReport(invoiceNo: string, startDate: string, endDate: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { invoiceNo, startDate, endDate };
    if (companyId) {
      params.companyId = companyId;
    }
    if (branchId) {
      params.branchId = branchId;
    }
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetWholesaleDetailRpt`, {
      params: params
    });
  }

  getWholesaleSummaryReport(startDate: string, endDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (customerId) params.customerId = customerId;
    if (companyId) params.companyId = companyId;
    if (branchId) params.branchId = branchId;
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetWholesaleSummaryRpt`, { params });
  }

  getWholesaleReturnSalesReport(startDate: string, endDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (customerId) params.customerId = customerId;
    if (companyId) params.companyId = companyId;
    if (branchId) params.branchId = branchId;
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetWholesaleReturnSalesRpt`, { params });
  }

  getWholesaleReturnSummaryReport(startDate: string, endDate: string, customerId?: string, companyId?: string, branchId?: string): Observable<any> {
    const params: any = { startDate, endDate };
    if (customerId) params.customerId = customerId;
    if (companyId) params.companyId = companyId;
    if (branchId) params.branchId = branchId;
    return this.http.get<any>(`${environment.apiUrl}/Reports/GetWholesaleReturnSummaryRpt`, { params });
  }
}
